import _ from 'lodash'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import User from '../../containers/User'
import attributes from '../../settings/references.json'
import Tooltip from '../Tooltip'
import Button from '../Button'
import styles from './user-list.scss'
import userStyles from '../User/user.scss'

// Map the list of users to the User component
const SortableItem = SortableElement(({ value, launchClient }) => (
    <User username={value} launchClient={launchClient} />
))

// Create the list of sortable items
const SortableList = SortableContainer(({ items, users, launchClient, layout }) => (
    <ul className={`${styles.userList} ${styles[layout]}`}>
        {items.map((value, index) => {
            // If the user doesn't exist in the user object
            // we don't create a new item
            if (Object.keys(users).indexOf(value) !== -1) {
                // TODO: Find out why I render so often.
                return (
                    <SortableItem
                        key={`item-${value}`}
                        index={index}
                        launchClient={launchClient}
                        value={value}
                    />
                )
            }
            return null
        })}
    </ul>
))

class UserList extends Component {
    constructor(props) {
        super(props)

        this.willUnmount = false
        this.launchDelayInterval = undefined
        this.timer = undefined
        this.autoLaunchStartup = undefined
        this.autoLaunchStartupInterval = undefined
        this.state = {
            launchClient: undefined,
            numberOfAccounts: 0,
            numberOfSelected: 0,
        }

        // Check if we're supposed to do auto launch on startup
        if (props.autoLaunchOnStartup === true) {
            this.autoLaunchStartup = true
        }
    }

    componentDidMount() {
        this.updateAccountCounters(undefined, true)
    }

    componentDidUpdate(prevProps) {
        if (!_.isEqual(prevProps.users, this.props.users)) {
            this.updateAccountCounters(prevProps)
        }

        if (prevProps.autoLaunchOnStartup !== this.props.autoLaunchOnStartup) {
            if (this.props.autoLaunchOnStartup) this.autoLaunchStartup = true
        }

        if (
            this.autoLaunchStartup &&
            (this.state.numberOfSelected > 0 || this.state.numberOfAccounts === 1)
        ) {
            this.runAutoLaunchOnStartup()
        }
    }

    componentWillUnmount() {
        this.willUnmount = true
        clearTimeout(this.timer)
        clearInterval(this.launchDelayInterval)
        this.launchDelayInterval = undefined
        this.timer = undefined
    }

    onSortEnd = ({ oldIndex, newIndex }) => {
        // Get the new sort order of items
        const order = arrayMove([...this.props.sortList], oldIndex, newIndex)

        // Dispatch an update for the localforage
        this.props.setOrder(_.uniq(order))
    }

    setStateIff = (state) => {
        if (this.willUnmount) return

        this.setState(state)
    }

    runAutoLaunchOnStartup() {
        if (!this.props.playEnabled) {
            console.info(
                'Auto-launch on startup detected. Could not start clients as launcher was updating files.',
            )
            return
        }

        console.info('Auto-launch on startup detected. Starting clients.')

        // Set auto-launch as false
        this.autoLaunchStartup = false

        // Update the URI
        this.props.updateLaunchOnStartup()

        // Run the command to start accounts
        this.startClientsMultiLaunch()
    }

    // Update the number of selected accounts
    updateAccountCounters = (prevProps, force = false) => {
        // If the props have been updated, or we receive a force command
        // we check if the number of multiLaunch tagged accounts has changed
        if (prevProps !== undefined || force) {
            // Get the number of accounts tagged with multilaunch
            const numberOfSelected = _.size(_.pickBy(this.props.users, user => user.multiLaunch))

            const numberOfAccounts = _.size(this.props.users)

            // Change the number in the state
            this.setStateIff({ numberOfAccounts, numberOfSelected })
        }
    }

    startClientsMultiLaunch() {
        // Get a list of all the active processes
        const activeProcesses = _.pickBy(this.props.processes, proc => proc.isRunning)

        // Only one account is logged in, so we start that one immediately
        if (_.size(this.props.users) === 1) {
            // Get the key of the first user object
            const userKey = Object.keys(this.props.users)[0]

            // Preventing play button from working on currently running process
            if (activeProcesses != null && activeProcesses[userKey] !== undefined) return

            if (this.timer === undefined) {
                console.info('Queued 1 account for start-up.')

                // Set timer to return timer to normal
                this.timer = setTimeout(() => {
                    this.setStateIff({ launchClient: undefined })
                    this.timer = undefined
                }, 100)

                // Launch the only client available
                this.setStateIff({
                    launchClient: this.props.users[userKey].username,
                })
            }
            return
        }

        // Use sortList to ensure correct launch order
        const accounts = [...this.props.sortList]

        // Pick the usernames out of the user list
        // where users have multiLaunch enabled
        const selectedUsers = _.map(
            _.pickBy(
                this.props.users,
                user =>
                    user.multiLaunch &&
                    (activeProcesses != null && activeProcesses[user.username] === undefined),
            ),
            'username',
        )

        // Remove any accounts from accounts that aren't in the selected users
        _.remove(accounts, username => selectedUsers.indexOf(username) === -1)

        // Launch delay between starting up clients
        const seconds = this.props.launchDelay * 1000

        // Reset the state
        this.setStateIff({ launchClient: undefined })

        // Make sure we only set the inteval if it is not already set
        // Another option would be to clear the running interval first and then set it again
        if (this.launchDelayInterval === undefined) {
            console.info(
                `Queued ${accounts.length} accounts for multi-launch: ${accounts.join(', ')}`,
            )

            // Run for first account without delay
            this.runMultiLaunch(accounts)

            // Set interval which will wait the allocated time
            // then run for any additional accounts
            this.launchDelayInterval = setInterval(() => this.runMultiLaunch(accounts), seconds)
        }
    }

    runMultiLaunch(accounts) {
        // If we have finished the list of accounts to start
        // kill the interval
        if (accounts.length > 0) {
            // Retrieve an account from the list
            const account = accounts.shift()

            // Set the state
            this.setStateIff({ launchClient: account })
        } else {
            this.setStateIff({ launchClient: undefined })
            clearInterval(this.launchDelayInterval)
            this.launchDelayInterval = undefined
        }
    }

    renderPlayButton() {
        // If the server is not ready...
        if (this.props.playEnabled !== undefined && !this.props.playEnabled) return null

        // Check whether we should display the button
        // only show when we have exactly one account
        // or if we have a more accounts AND at least one selected
        const displayButton =
            this.state.numberOfAccounts === 1 ||
            (this.state.numberOfAccounts > 1 && this.state.numberOfSelected > 0)

        if (!displayButton) return null

        // Get the number of selected accounts for multi-launch
        const selectedAccounts =
            this.state.numberOfSelected > 0 && this.state.numberOfAccounts > 1
                ? ` (${this.state.numberOfSelected})`
                : null

        return (
            <Tooltip content={this.props.localize('playTooltip')} style={{ marginRight: '10px' }}>
                <Button inline onClick={() => this.startClientsMultiLaunch()}>
                    {this.props.localize('play')}
                    {selectedAccounts}
                </Button>
            </Tooltip>
        )
    }

    renderAddAccountButton = () => (
        <Tooltip content={this.props.localize('addTooltip')}>
            <Button onClick={() => this.props.displayLoginWindow(true)}>
                {`+ ${this.props.localize('addAccount')}`}
            </Button>
        </Tooltip>
    )

    render() {
        try {
            // If no server is defined, return nothing
            if (this.props.server == null || this.props.users == null) return null

            // Prepare the sorted list
            let sortList = this.props.sortList

            // Check whether the sort list is missing or has no items
            if (
                (sortList == null || sortList.length === 0) &&
                Object.keys(this.props.users).length > 0
            ) {
                // If empty or missing replace with array of usernames in users
                sortList = Object.keys(this.props.users)

                // Register a log entry stating that we're using the object keys instead
                console.info('User order list was missing, replacing with list of all users')
            }

            return (
                <div>
                    <SortableList
                        items={sortList}
                        users={this.props.users}
                        launchClient={this.state.launchClient}
                        helperClass={userStyles.moving}
                        lockAxis="y"
                        onSortEnd={this.onSortEnd}
                        pressDelay={attributes.userList.pressDelayMilliseconds}
                        layout={this.props.layout}
                    />
                    <div className={styles.actions}>
                        {this.renderPlayButton()}
                        {this.renderAddAccountButton()}
                    </div>
                </div>
            )
        } catch (error) {
            console.info(`Encountered an error while rendering user-list: ${error.message}`)
        }
    }
}

UserList.propTypes = {
    server: PropTypes.string,
    users: PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.shape({
            hash: PropTypes.string,
            profile: PropTypes.string,
            remember: PropTypes.bool,
            multiLaunch: PropTypes.bool,
            tokens: PropTypes.shape({
                accessToken: PropTypes.string,
                refreshToken: PropTypes.string,
            }),
            username: PropTypes.string,
        }),
    }),
    processes: PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.shape({
            isRunning: PropTypes.bool,
            progress: PropTypes.number,
        }),
    }),
    launchDelay: PropTypes.number,
    playEnabled: PropTypes.bool,
    sortList: PropTypes.arrayOf(PropTypes.string),
    setOrder: PropTypes.func,
    displayLoginWindow: PropTypes.func,
    localize: PropTypes.func,
    layout: PropTypes.string,
    autoLaunchOnStartup: PropTypes.bool,
    updateLaunchOnStartup: PropTypes.func,
}

export default UserList



// WEBPACK FOOTER //
// ./src/components/UserList/user-list.js